import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { CurrentPlanCard } from "@/features/billing/components/CurrentPlanCard";
import { AddonsSection } from "@/features/billing/components/AddonsSection";
import { ManageSubscriptionModal } from "@/features/billing/components/ManageSubscriptionModal";
import { AddonConfirmationDialog } from "@/features/billing/components/AddonConfirmationDialog";
import { InvoiceHistory } from "@/features/billing/components/InvoiceHistory";
import { ReferralDiscountDetails } from "@/components/ReferralDiscountDetails";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { CreditCard, Receipt, Gift } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRequireAdmin } from "@/shared/hooks/useRoleGuard";
import { handleCheckout } from "@/shared/utils/checkoutHandler";
import { apiClient } from "@/shared/services/apiClient";

export default function BillingPage() {
  // Admin-only access guard
  useRequireAdmin();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const { usage, loading: subscriptionLoading, refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activeButton, setActiveButton] = useState<string | null>(null);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAddon, setPendingAddon] = useState<{ type: string; price: number } | null>(null);
  const checkoutCleanupRef = useRef<(() => void) | null>(null);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const loadingStartTimeRef = useRef<number>(Date.now());
  const [activeTab, setActiveTab] = useState("subscription");
  const [hasReferralCode, setHasReferralCode] = useState(false);

  const footageTooltip = "Videos are saved for 30 days from the day they are filmed.";

  // Force refresh subscription data when billing page loads
  useEffect(() => {
    console.log('[BillingPage] Mounting - forcing subscription refresh to ensure fresh data');
    console.log('[BillingPage] Current usage state before refresh:', {
      hasUsage: !!usage,
      subscribed: usage?.subscribed,
      loading: subscriptionLoading,
      usage: usage
    });
    refreshSubscription();

    // Check for referral code
    const checkReferralCode = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const referralCode = user?.user_metadata?.referral_code || localStorage.getItem('pendingReferralCode');
      setHasReferralCode(!!referralCode);
    };
    checkReferralCode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - refreshSubscription is stable but eslint wants it in deps

  // Cleanup checkout handler on unmount
  useEffect(() => {
    return () => {
      if (checkoutCleanupRef.current) {
        checkoutCleanupRef.current();
        checkoutCleanupRef.current = null;
      }
    };
  }, []);

  // Log whenever usage changes
  useEffect(() => {
    console.log('[BillingPage] Usage state updated:', {
      hasUsage: !!usage,
      subscribed: usage?.subscribed,
      isTrialActive: usage?.isTrialActive,
      subscription_status: usage?.subscription_status,
      planType: usage?.planType,
      loading: subscriptionLoading
    });
  }, [usage, subscriptionLoading]);

  // All polling logic removed - now using unified checkout handler with PostMessage

  // Loading timeout protection - prevent infinite loading
  useEffect(() => {
    const LOADING_TIMEOUT = 15000; // 15 seconds

    const timeoutId = setTimeout(() => {
      if (subscriptionLoading && !usage) {
        console.warn('[BillingPage] ⚠️ Loading timeout reached');
        setLoadingTimedOut(true);
      }
    }, LOADING_TIMEOUT);

    // Clear timeout if loading completes
    if (!subscriptionLoading || usage) {
      setLoadingTimedOut(false);
    }

    return () => clearTimeout(timeoutId);
  }, [subscriptionLoading, usage]);

  // Cleanup handled by checkoutCleanupRef in the unified handler

  // OPTIMIZED: Auth check removed - already handled by AuthContext and app-level routing

  // Show loading state with timeout protection
  if ((subscriptionLoading || !usage) && !processingPayment) {
    if (loadingTimedOut) {
      // Show error after timeout
      return (
        <div className="container mx-auto p-6 max-w-6xl">
          <div className="text-center space-y-4">
            <div className="text-lg text-destructive">Loading Timeout</div>
            <p className="text-sm text-muted-foreground">
              Unable to load subscription data. This might be a temporary issue.
            </p>
            <button
              onClick={() => {
                setLoadingTimedOut(false);
                loadingStartTimeRef.current = Date.now();
                refreshSubscription();
              }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    // Show normal loading
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <div className="text-lg">Loading subscription data...</div>
          <p className="text-sm text-muted-foreground mt-2">
            {Math.floor((Date.now() - loadingStartTimeRef.current) / 1000)}s elapsed
          </p>
        </div>
      </div>
    );
  }

  const handleBuyAddon = (addonType: string, price: number) => {
    // Store pending addon and open confirmation dialog
    setPendingAddon({ type: addonType, price });
    setConfirmDialogOpen(true);
  };

  const handleConfirmAddon = async () => {
    if (!pendingAddon) return;

    const buttonKey = `addon-${pendingAddon.type}`;
    setActiveButton(buttonKey);
    setLoading(true);
    setConfirmDialogOpen(false);

    try {
      // Get user data for addon purchase
      const { data: { user } } = await supabase.auth.getUser();

      console.log('[BillingPage] Calling create-payment via backend proxy with:', {
        dataPackage: pendingAddon.type,
        quantity: 1
      });

      // Use create-payment for adding addons to existing subscriptions
      const data = await apiClient.post<{ success: boolean; url?: string; message?: string }>('/edge/create-payment', {
        dataPackage: pendingAddon.type, // storage_90_days, storage_1_year, extra_user
        quantity: 1,
        userData: {
          id: user?.id,
          email: user?.email,
          user_metadata: user?.user_metadata
        }
      });

      console.log('[BillingPage] create-payment response:', { data });

      // Case 1: Trial user - need to complete checkout (base plan + addon)
      if (data.success && data.url) {
        console.log('[BillingPage] Trial user addon - opening checkout in new tab');

        setLoading(false);
        setActiveButton(null);
        setProcessingPayment(true);
        setPendingAddon(null);

        // Use unified checkout handler
        const { cleanup } = handleCheckout({
          checkoutUrl: data.url,
          onSuccess: async () => {
            console.log('[BillingPage] ✅ Addon payment successful!');

            // Immediately hide processing modal
            setProcessingPayment(false);

            // Show success toast
            toast({
              title: "Add-on Added!",
              description: "Your addon has been added successfully!",
            });

            // Refresh subscription in background
            try {
              await refreshSubscription();
            } catch (error) {
              console.error('[BillingPage] Error refreshing subscription:', error);
            }
          },
          onError: (errorMessage) => {
            console.log('[BillingPage] ❌ Addon payment failed:', errorMessage);
            toast({
              title: "Payment Failed",
              description: errorMessage || "Please try again or contact support.",
              variant: "destructive",
            });
            setProcessingPayment(false);
          },
          onClose: () => {
            console.log('[BillingPage] Addon checkout window closed');
            toast({
              title: "Payment Canceled",
              description: "The checkout was canceled. No charges were made.",
              variant: "default",
            });
            setProcessingPayment(false);
            refreshSubscription();
          },
        });

        // Store cleanup function
        checkoutCleanupRef.current = cleanup;
      }
      // Case 2: Paid user - addon added directly to subscription
      else if (data.success && data.message) {
        toast({
          title: "Add-on Added!",
          description: data.message,
        });
        setLoading(false);
        setActiveButton(null);
        setPendingAddon(null);

        // FIXED: Use refreshSubscription instead of hard page reload
        await refreshSubscription();
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error: any) {
      console.error('Error adding addon:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add addon to subscription",
        variant: "destructive"
      });
    } finally {
      // Always reset loading states, even when errors occur
      setLoading(false);
      setProcessingPayment(false);
      setActiveButton(null);
      setPendingAddon(null);
    }
  };

  const handleManageSubscription = () => {
    setManageModalOpen(true);
  };

  const handleManageSuccess = () => {
    refreshSubscription();
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Use success/error pages for PostMessage communication
      // Use VITE_APP_BASE_URL for production or window.location.origin for local dev
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
      const successUrl = `${baseUrl}/subscription-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/subscription-error?message=Payment was canceled`;

      console.log('[BillingPage] Creating checkout with URLs:', { baseUrl, successUrl, cancelUrl });

      // Get current user to check for referral code
      const { data: { user } } = await supabase.auth.getUser();
      const referralCode = user?.user_metadata?.referral_code || localStorage.getItem('pendingReferralCode');

      console.log('[BillingPage] Referral check:', { hasReferralCode: !!referralCode, referralCode });

      const data = await apiClient.post<{ url: string }>('/edge/create-checkout', {
        userCount: 1, // Single admin user subscription
        successUrl,
        cancelUrl,
        ...(referralCode && {
          referral_code: referralCode, // ✅ Pass referral code to apply $100 discount
          skip_trial: true, // ✅ Skip trial for referred users
        }),
        userData: {
          id: user?.id,
          email: user?.email,
          user_metadata: user?.user_metadata
        }
      });

      if (!data?.url) throw new Error('No checkout URL returned');

      console.log('[BillingPage] Got checkout URL:', data.url);

      setLoading(false);
      setProcessingPayment(true);

      // Use unified checkout handler
      const { cleanup } = handleCheckout({
        checkoutUrl: data.url,
        onSuccess: async () => {
          console.log('[BillingPage] ✅ Payment successful!');

          // Immediately hide processing modal
          setProcessingPayment(false);

          // Show success toast
          toast({
            title: "Payment Successful!",
            description: "Your subscription is now active. Thank you!",
          });

          // Refresh subscription in background
          try {
            await refreshSubscription();
          } catch (error) {
            console.error('[BillingPage] Error refreshing subscription:', error);
          }
        },
        onError: (errorMessage) => {
          console.log('[BillingPage] ❌ Payment failed:', errorMessage);
          toast({
            title: "Payment Failed",
            description: errorMessage || "Please try again or contact support.",
            variant: "destructive",
          });
          setProcessingPayment(false);
        },
        onClose: () => {
          console.log('[BillingPage] Checkout window closed');
          toast({
            title: "Payment Canceled",
            description: "The checkout was canceled. No charges were made.",
            variant: "default",
          });
          setProcessingPayment(false);
          refreshSubscription();
        },
      });

      // Store cleanup function
      checkoutCleanupRef.current = cleanup;
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive"
      });
    } finally {
      // Always reset loading states, even when errors occur
      setLoading(false);
      setProcessingPayment(false);
    }
  };

  const getAddonDescription = (addonType: string) => {
    switch (addonType) {
      case 'extra_user':
        return 'Extra User - Add team members to your workspace';
      case 'storage_90_days':
        return '90 Day Storage - Save your videos for 90 days from the day they were filmed.';
      case 'storage_1_year':
        return '1 Year Storage - Save your videos for 365 days from the day they were filmed.';
      default:
        return 'Additional service';
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {processingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <h3 className="text-lg font-semibold">Processing Payment...</h3>
            <p className="text-sm text-muted-foreground">
              Please complete your payment in the new tab. This page will update automatically.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">wlkthru Pro</h1>
          <p className="text-muted-foreground">Your subscription plan and billing details</p>
        </div>

        {/* ✅ NEW: Show referral discount CTA for non-subscribers with referral code */}
        {!usage?.subscribed && hasReferralCode && (
          <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Gift className="h-6 w-6 text-green-600" />
                <CardTitle className="text-green-900">Special Referral Offer</CardTitle>
              </div>
              <CardDescription className="text-green-700">
                You've been referred to wlkthru! Activate your discount now.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <p className="text-2xl font-bold text-green-600 mb-2">Save $100</p>
                <p className="text-gray-700">
                  Pay only <strong>$66/month</strong> for your first 3 months
                  <span className="block text-sm text-gray-500 mt-1">Regular price: <span className="line-through">$99/month</span></span>
                </p>
              </div>

              <Button
                onClick={handleSubscribe}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-6 text-lg"
                disabled={loading || processingPayment}
              >
                {loading || processingPayment ? 'Loading...' : 'Subscribe Now & Save $100'}
              </Button>

              <p className="text-xs text-gray-600 text-center">
                Your referral discount will be applied automatically at checkout
              </p>
            </CardContent>
          </Card>
        )}

        <ReferralDiscountDetails />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="subscription" className="flex items-center gap-2">
              
              Subscription
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2" disabled={!usage?.subscribed}>
           
              Invoices
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscription" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* Current Plan Section */}
              <CurrentPlanCard
                usage={usage as any || {}}
                onManageSubscription={handleManageSubscription}
                onSubscribe={handleSubscribe}
                loading={loading}
                processingPayment={processingPayment}
                footageTooltip={footageTooltip}
              />

              {/* Add-ons Section - Only show if subscribed (paid trial or active paid) */}
              {usage?.subscribed && (
                <AddonsSection
                  onBuyAddon={handleBuyAddon}
                  loading={loading}
                  activeButton={activeButton}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            {usage?.subscribed && <InvoiceHistory />}
          </TabsContent>
        </Tabs>
      </div>

      <ManageSubscriptionModal
        open={manageModalOpen}
        onOpenChange={setManageModalOpen}
        onSuccess={handleManageSuccess}
      />

      <AddonConfirmationDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        onConfirm={handleConfirmAddon}
        addonType={pendingAddon?.type as 'extra_user' | 'storage_90_days' | 'storage_1_year'}
        addonPrice={pendingAddon?.price}
        isTrialUser={usage?.isTrialActive || false}
        daysLeftInTrial={usage?.daysLeftInTrial}
        loading={loading}
        currentUsers={usage?.currentUsers}
        maxUsers={usage?.maxUsers}
      />
    </div>
  );
}
